<script>
import Layout from '@layouts/main'

import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import VueApexCharts from 'vue-apexcharts'
import serviceContaPagar from '@/src/services/conta-pagar-receber'
import moment from 'moment'

moment.locale('pt-br')

export default {
  components: { Layout, Loading, apexchart: VueApexCharts },
  data() {
    return {
      currentMonth: '01',
      cashFlow: [],
      isLoading: false,
      monthOptions: [
        { value: '01', text: 'Janeiro' },
        { value: '02', text: 'Fevereiro' },
        { value: '03', text: 'Marco' },
        { value: '04', text: 'Abril' },
        { value: '05', text: 'Maio' },
        { value: '06', text: 'Junho' },
        { value: '07', text: 'Julho' },
        { value: '08', text: 'Agosto' },
        { value: '09', text: 'Setembro' },
        { value: '10', text: 'Outubro' },
        { value: '11', text: 'Novembro' },
        { value: '12', text: 'Dezembro' },
      ],
      cashFlowFields: [
        { key: 'data', label: 'Data' },
        { key: 'recebimentos', label: 'Recebimentos', class: 'text-right' },
        { key: 'pagamentos', label: 'Pagamentos', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
      ],
      columnsExcel: [
        { label: 'Data', field: 'data_formatada' },
        { label: 'Recebimentos', field: 'recebimentos_formatado' },
        { label: 'Pagamentos', field: 'pagamentos_formatado' },
        { label: 'Saldo', field: 'saldo_formatado' },
      ],
      pieSeries: [],
      pieOptions: {
        colors: ['#0b9a7d', '#db5b57'],
        chart: {
          type: 'donut',
          toolbar: { show: false },
        },
        labels: ['Recebimentos', 'Pagamentos'],
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
      trendSeries: [],
      trendOptions: {
        chart: {
          type: 'line',
          height: 360,
          stacked: false,
          toolbar: { show: false },
        },
        stroke: {
          width: [0, 0, 3],
          curve: 'smooth',
        },
        plotOptions: {
          bar: {
            columnWidth: '42%',
            borderRadius: 6,
          },
        },
        colors: ['#0b9a7d', '#db5b57', '#11493f'],
        dataLabels: {
          enabled: false,
        },
        labels: [],
        xaxis: {
          type: 'category',
        },
        yaxis: [
          {
            labels: {
              formatter: (value) => this.formatCurrency(value),
            },
          },
          {
            opposite: true,
            labels: {
              formatter: (value) => this.formatCurrency(value),
            },
          },
        ],
        legend: {
          position: 'top',
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => this.formatCurrency(value),
          },
        },
      },
    }
  },
  computed: {
    rows() {
      return this.cashFlow.length
    },
    selectedMonthLabel() {
      const selected = this.monthOptions.find(
        (item) => item.value === this.currentMonth
      )
      return selected ? selected.text : ''
    },
    totalRecebimentos() {
      return this.cashFlow.reduce(
        (total, item) => total + this.parseCurrency(item.recebimentos),
        0
      )
    },
    totalPagamentos() {
      return this.cashFlow.reduce(
        (total, item) => total + this.parseCurrency(item.pagamentos),
        0
      )
    },
    saldoPeriodo() {
      return this.totalRecebimentos - this.totalPagamentos
    },
    melhorDia() {
      if (!this.cashFlow.length) {
        return null
      }

      return [...this.cashFlow]
        .map((item) => ({
          ...item,
          saldoNumerico: this.parseSaldoValue(item.saldo),
        }))
        .sort((a, b) => b.saldoNumerico - a.saldoNumerico)[0]
    },
    piorDia() {
      if (!this.cashFlow.length) {
        return null
      }

      return [...this.cashFlow]
        .map((item) => ({
          ...item,
          saldoNumerico: this.parseSaldoValue(item.saldo),
        }))
        .sort((a, b) => a.saldoNumerico - b.saldoNumerico)[0]
    },
    excelData() {
      return this.cashFlow.map((item) => ({
        data_formatada: this.formatDate(item.data),
        recebimentos_formatado: this.formatCurrency(
          this.parseCurrency(item.recebimentos)
        ),
        pagamentos_formatado: this.formatCurrency(
          this.parseCurrency(item.pagamentos)
        ),
        saldo_formatado: this.formatCurrency(this.parseSaldoValue(item.saldo)),
      }))
    },
  },
  mounted() {
    this.currentMonth = moment().utcOffset('-03:00').format('MM')
    this.listando(this.currentMonth)
  },
  methods: {
    listando(month) {
      const token = this.$store.state.auth.currentUser.token
      this.currentMonth = month
      this.isLoading = true

      serviceContaPagar
        .findAllFlowAccount(token, month)
        .then((resposta) => {
          this.cashFlow = resposta.data.contas || []
          this.pieSeries = [
            this.parseCurrency(resposta.data.chart.recebimentos),
            this.parseCurrency(resposta.data.chart.pagamentos),
          ]
          this.updateTrendChart()
        })
        .catch((error) => {
          this.$bvToast.toast(
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : 'Nao foi possivel carregar o fluxo de caixa.',
            {
              variant: 'danger',
              solid: false,
              noCloseButton: true,
              toaster: 'b-toaster-top-center',
            }
          )
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateTrendChart() {
      this.trendSeries = [
        {
          name: 'Recebimentos',
          type: 'column',
          data: this.cashFlow.map((item) => this.parseCurrency(item.recebimentos)),
        },
        {
          name: 'Pagamentos',
          type: 'column',
          data: this.cashFlow.map((item) => this.parseCurrency(item.pagamentos)),
        },
        {
          name: 'Saldo acumulado',
          type: 'line',
          data: this.cashFlow.map((item) => this.parseSaldoValue(item.saldo)),
        },
      ]

      this.trendOptions = {
        ...this.trendOptions,
        labels: this.cashFlow.map((item) => this.formatDate(item.data)),
      }
    },
    formatDate(date) {
      return moment.utc(date).format('DD/MM/YYYY')
    },
    parseCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }

      if (typeof value === 'number') {
        return value
      }

      const cleaned = String(value)
        .replace(/R\$\s?/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
        .trim()

      const parsed = Number(cleaned)
      return Number.isFinite(parsed) ? parsed : 0
    },
    parseSaldoValue(saldo) {
      if (saldo && typeof saldo === 'object' && saldo.valor !== undefined) {
        return this.parseCurrency(saldo.valor)
      }

      return this.parseCurrency(saldo)
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(this.parseCurrency(value))
    },
    formatSaldo(item) {
      return this.formatCurrency(this.parseSaldoValue(item.saldo))
    },
    saldoClass(item) {
      const saldo = this.parseSaldoValue(item.saldo)
      if (saldo < 0) {
        return 'cash-badge is-negative'
      }

      if (saldo === 0) {
        return 'cash-badge is-neutral'
      }

      return 'cash-badge is-positive'
    },
    monthSubtitle() {
      return `Consolidado de ${this.selectedMonthLabel.toLowerCase()}`
    },
  },
}
</script>

<template>
  <Layout>
    <div class="cash-shell">
      <section class="cash-hero">
        <div>
          <span class="cash-eyebrow">Financeiro</span>
          <h1 class="cash-title">Fluxo de Caixa</h1>
          <p class="cash-subtitle">
            {{ monthSubtitle() }} com leitura mais clara de entradas, saidas e
            saldo projetado por dia.
          </p>
        </div>
        <div class="cash-hero-actions">
          <button class="btn btn-light" @click="$router.push('/financeiro/dashboard')">
            Dashboard
          </button>
          <button class="btn btn-primary" @click="$refs.btnexport.$el.click()">
            Exportar Excel
            <vue-excel-xlsx
              ref="btnexport"
              style="display: none"
              :data="excelData"
              :columns="columnsExcel"
              :filename="'fluxo_caixa'"
              :sheetname="'fluxo_caixa'"
            />
          </button>
        </div>
      </section>

      <section class="cash-filter-panel">
        <div class="cash-filter-row">
          <b-form-group label="Mes de referencia" class="mb-0">
            <b-form-select
              v-model="currentMonth"
              :options="monthOptions"
              @change="listando"
            />
          </b-form-group>
          <div class="cash-filter-helper">
            <strong>{{ rows }}</strong>
            <span>linhas no fluxo diario deste mes</span>
          </div>
        </div>
      </section>

      <div class="cash-kpi-grid">
        <article class="cash-kpi-card is-positive">
          <span class="cash-kpi-label">Recebimentos</span>
          <strong class="cash-kpi-value">{{ formatCurrency(totalRecebimentos) }}</strong>
        </article>
        <article class="cash-kpi-card is-negative">
          <span class="cash-kpi-label">Pagamentos</span>
          <strong class="cash-kpi-value">{{ formatCurrency(totalPagamentos) }}</strong>
        </article>
        <article class="cash-kpi-card is-balance">
          <span class="cash-kpi-label">Saldo do periodo</span>
          <strong class="cash-kpi-value">{{ formatCurrency(saldoPeriodo) }}</strong>
        </article>
        <article class="cash-kpi-card is-highlight">
          <span class="cash-kpi-label">Melhor dia</span>
          <strong class="cash-kpi-value">
            {{ melhorDia ? formatDate(melhorDia.data) : '--' }}
          </strong>
          <small v-if="melhorDia">{{ formatSaldo(melhorDia) }}</small>
        </article>
        <article class="cash-kpi-card is-warning">
          <span class="cash-kpi-label">Dia mais pressionado</span>
          <strong class="cash-kpi-value">
            {{ piorDia ? formatDate(piorDia.data) : '--' }}
          </strong>
          <small v-if="piorDia">{{ formatSaldo(piorDia) }}</small>
        </article>
      </div>

      <div class="cash-chart-grid">
        <section class="cash-panel">
          <div class="cash-panel-head">
            <div>
              <h2>Distribuicao do mes</h2>
              <p>Comparativo rapido entre entradas e saidas do periodo.</p>
            </div>
          </div>
          <div v-if="pieSeries[0] > 0 || pieSeries[1] > 0" class="cash-chart-wrap is-donut">
            <apexchart
              type="donut"
              height="320"
              :options="pieOptions"
              :series="pieSeries"
            />
          </div>
          <div v-else class="cash-empty-state">
            Nenhuma movimentacao financeira para exibir no grafico.
          </div>
        </section>

        <section class="cash-panel">
          <div class="cash-panel-head">
            <div>
              <h2>Recebimentos, pagamentos e saldo</h2>
              <p>Leitura diaria com barras para fluxo e linha para saldo acumulado.</p>
            </div>
          </div>
          <div v-if="cashFlow.length" class="cash-chart-wrap">
            <apexchart
              type="line"
              height="320"
              :options="trendOptions"
              :series="trendSeries"
            />
          </div>
          <div v-else class="cash-empty-state">
            Sem dados para montar a evolucao diaria deste mes.
          </div>
        </section>
      </div>

      <section class="cash-panel">
        <div class="cash-panel-head">
          <div>
            <h2>Fluxo diario detalhado</h2>
            <p>Tabela consolidada para auditoria rapida do caixa.</p>
          </div>
        </div>

        <b-table
          responsive
          hover
          small
          :items="cashFlow"
          :fields="cashFlowFields"
          show-empty
          class="cash-table"
        >
          <template #empty>
            <div class="cash-empty-state">
              Nenhum movimento financeiro encontrado para {{ selectedMonthLabel.toLowerCase() }}.
            </div>
          </template>
          <template #cell(data)="row">
            {{ formatDate(row.item.data) }}
          </template>
          <template #cell(recebimentos)="row">
            <span class="cash-value is-positive">
              + {{ formatCurrency(row.item.recebimentos) }}
            </span>
          </template>
          <template #cell(pagamentos)="row">
            <span class="cash-value is-negative">
              - {{ formatCurrency(row.item.pagamentos) }}
            </span>
          </template>
          <template #cell(saldo)="row">
            <span :class="saldoClass(row.item)">
              {{ formatSaldo(row.item) }}
            </span>
          </template>
        </b-table>
      </section>

      <loading :active.sync="isLoading" color="#008e75" :can-cancel="false" />
    </div>
  </Layout>
</template>

<style lang="scss">
.cash-shell {
  display: flex;
  flex-direction: column;
  gap: 22px;
  margin-top: 30px;
}

.cash-hero,
.cash-filter-panel,
.cash-panel,
.cash-kpi-card {
  background: #fff;
  border: 1px solid rgba(0, 142, 117, 0.14);
  border-radius: 20px;
  box-shadow: 0 18px 34px rgba(15, 61, 52, 0.06);
}

.cash-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 30px;
}

.cash-eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #008e75;
}

.cash-title {
  margin: 0 0 8px;
  font-size: 34px;
  color: #11493f;
}

.cash-subtitle {
  margin: 0;
  color: #64807a;
}

.cash-hero-actions {
  display: flex;
  gap: 12px;
}

.cash-filter-panel {
  padding: 18px 22px;
}

.cash-filter-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
}

.cash-filter-helper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #69847f;
}

.cash-filter-helper strong {
  font-size: 28px;
  color: #11493f;
  line-height: 1;
}

.cash-kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
}

.cash-kpi-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 22px;
}

.cash-kpi-card.is-positive {
  background: linear-gradient(180deg, #f2fbf8 0%, #ffffff 100%);
}

.cash-kpi-card.is-negative {
  background: linear-gradient(180deg, #fff5f3 0%, #ffffff 100%);
}

.cash-kpi-card.is-balance {
  background: linear-gradient(180deg, #f4f7ff 0%, #ffffff 100%);
}

.cash-kpi-card.is-highlight {
  background: linear-gradient(180deg, #f7fbfa 0%, #ffffff 100%);
}

.cash-kpi-card.is-warning {
  background: linear-gradient(180deg, #fff9ee 0%, #ffffff 100%);
}

.cash-kpi-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #708a84;
}

.cash-kpi-value {
  font-size: 28px;
  line-height: 1.15;
  color: #11493f;
}

.cash-kpi-card small {
  color: #64807a;
}

.cash-chart-grid {
  display: grid;
  grid-template-columns: minmax(320px, 0.9fr) minmax(0, 1.1fr);
  gap: 20px;
}

.cash-panel {
  padding: 24px;
}

.cash-panel-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.cash-panel-head h2 {
  margin: 0 0 6px;
  color: #11493f;
}

.cash-panel-head p {
  margin: 0;
  color: #69847f;
}

.cash-chart-wrap {
  min-height: 320px;
}

.cash-chart-wrap.is-donut {
  display: flex;
  justify-content: center;
}

.cash-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  text-align: center;
  color: #6f8a84;
}

.cash-table table {
  margin-bottom: 0;
}

.cash-value {
  font-weight: 700;
}

.cash-value.is-positive {
  color: #0b9a7d;
}

.cash-value.is-negative {
  color: #db5b57;
}

.cash-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
}

.cash-badge.is-positive {
  background: rgba(11, 154, 125, 0.12);
  color: #0b9a7d;
}

.cash-badge.is-negative {
  background: rgba(219, 91, 87, 0.12);
  color: #c24f4b;
}

.cash-badge.is-neutral {
  background: rgba(17, 73, 63, 0.08);
  color: #11493f;
}

@media (max-width: 1199px) {
  .cash-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cash-chart-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .cash-hero,
  .cash-filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .cash-hero-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .cash-kpi-grid {
    grid-template-columns: 1fr;
  }

  .cash-filter-helper {
    align-items: flex-start;
  }
}
</style>
